import React from 'react';
import { Modal } from 'components/Modal';
import { Button } from 'components/Button';
import { FixedRule } from 'components/FixedRule';
import { RuleItem } from 'types/plans';
import { CashIcon, PlusIcon } from '@heroicons/react/outline';

type FixedModalProps = {
  show: boolean;
  setShowModal: (value: boolean) => void;
  rules: RuleItem[];
  addNewRule: () => void;
  removeRule: (index: number) => void;
  onChangeRuleValue: (field: string, value: string, index: number) => void;
  handleSetRule?: (type: string) => void;
  isLoading?: boolean;
  defaultFee: string;
  setDefaultFee: (fee: string) => void;
  isDisabled?: boolean;
};

export const FixedModal = ({
  show,
  setShowModal,
  rules,
  addNewRule,
  removeRule,
  onChangeRuleValue,
  handleSetRule,
  isLoading,
  defaultFee,
  setDefaultFee,
  isDisabled,
}: FixedModalProps) => {
  const handleSave = () => {
    handleSetRule && handleSetRule('fixed');
  };

  const handleFieldUpdate = (value: string) => {
    const num = Number(value);
    if (isNaN(num)) {
      return;
    }
    setDefaultFee(value);
  };

  return (
    <Modal
      openModal={(value) => setShowModal(value)}
      open={show}
      containerClass="w-full max-w-3xl !p-0 overflow-hidden"
      iconClassName="text-[#64748b] hover:text-[#0b1c30]"
    >
      <div className="px-6 pb-6 pt-1">
        <div className="mb-6 flex items-start gap-4">
          <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-[#eef5ff] text-[#2563eb]">
            <CashIcon className="h-6 w-6" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#0b1c30]">
              Fixed Guarantee Fee
            </h2>
            <p className="mt-1 text-sm leading-6 text-[#64748b]">
              Charge a static fee for every order, with optional rules for
              specific order value ranges.
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-[#d8dfeb] bg-[#f8fbff] p-4">
          <label className="mb-2 block text-xs font-bold uppercase tracking-wide text-[#737686]">
            Default fee
          </label>
          <input
            name="defaultFee"
            className="w-full rounded-lg border border-[#d8dfeb] bg-white px-4 py-3 text-sm font-semibold text-[#0b1c30] shadow-sm focus:border-[#2563eb] focus:ring-1 focus:ring-[#2563eb] disabled:bg-slate-100 disabled:text-slate-400"
            placeholder="e.g 3.00"
            value={defaultFee}
            disabled={isDisabled}
            onChange={(e) =>
              handleFieldUpdate(e.target.value.replace(/[$£€CA$]/g, ''))
            }
          />
        </div>

        <div className="mt-5">
          <div className="mb-3 flex items-center justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-[#0b1c30]">
                Value-based rules
              </h3>
              <p className="mt-1 text-xs leading-5 text-[#64748b]">
                Add optional fee overrides for specific order totals.
              </p>
            </div>
            <button
              type="button"
              disabled={isDisabled}
              onClick={() => addNewRule()}
              className="inline-flex items-center gap-2 rounded-lg border border-[#d8dfeb] bg-white px-3 py-2 text-sm font-semibold text-[#2563eb] shadow-sm transition hover:border-[#b9cdf7] hover:bg-[#f8fbff] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <PlusIcon className="h-4 w-4" />
              Add rule
            </button>
          </div>

          <div className="max-h-[360px] space-y-3 overflow-y-auto rounded-xl border border-[#d8dfeb] bg-[#f8fbff] p-3">
            {rules.length > 0 ? (
              rules.map((rule, index) => (
                <FixedRule
                  key={index}
                  onRemoveItem={() => removeRule(index)}
                  rule={rule}
                  onChange={(field, value) =>
                    onChangeRuleValue(field, value, index)
                  }
                  disabled={isDisabled}
                />
              ))
            ) : (
              <div className="rounded-lg border border-dashed border-[#d8dfeb] bg-white p-6 text-center text-sm text-[#737686]">
                No value-based rules added.
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t border-[#e5ebf4] bg-[#f8fbff] px-6 py-4">
        <button
          type="button"
          disabled={isDisabled}
          onClick={() => setShowModal(false)}
          className="rounded-lg border border-[#d8dfeb] bg-white px-4 py-2 text-sm font-semibold text-[#334155] shadow-sm transition hover:bg-[#eff4ff] disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </button>
        <Button
          disabled={isDisabled}
          onClick={handleSave}
          className="rounded-lg bg-[#2563eb] px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#1d4ed8]"
          isLoading={isLoading}
        >
          Save changes
        </Button>
      </div>
    </Modal>
  );
};
